import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Edit } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EditSDPModal({ open, onClose, onSuccess, sdpId, token, field, currentPrioridade, currentJustificativa }) {
  const [loading, setLoading] = useState(false);
  const [prioridade, setPrioridade] = useState(currentPrioridade || "media");
  const [justificativa, setJustificativa] = useState(currentJustificativa || "");

  useEffect(() => {
    if (open) {
      setPrioridade(currentPrioridade || "media");
      setJustificativa(currentJustificativa || "");
    }
  }, [open, currentPrioridade, currentJustificativa]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = field === 'prioridade' 
        ? { prioridade } 
        : { justificativa };
      
      await axios.put(`${API_URL}/api/sdp/${sdpId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(field === 'prioridade' ? "Prioridade alterada!" : "Justificativa atualizada!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            {field === 'prioridade' ? 'Alterar Prioridade' : 'Editar Justificativa'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {field === 'prioridade' ? (
            <RadioGroup 
              value={prioridade} 
              onValueChange={setPrioridade}
              className="grid grid-cols-4 gap-3"
            >
              <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${prioridade === 'baixa' ? 'border-green-500 bg-green-50' : ''}`}>
                <RadioGroupItem value="baixa" id="edit_prioridade_baixa" className="sr-only" />
                <Label htmlFor="edit_prioridade_baixa" className="cursor-pointer text-center">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block mb-1"></span>
                  <span className="block font-medium">Baixa</span>
                </Label>
              </div>
              <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${prioridade === 'media' ? 'border-yellow-500 bg-yellow-50' : ''}`}>
                <RadioGroupItem value="media" id="edit_prioridade_media" className="sr-only" />
                <Label htmlFor="edit_prioridade_media" className="cursor-pointer text-center">
                  <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block mb-1"></span>
                  <span className="block font-medium">Media</span>
                </Label>
              </div>
              <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${prioridade === 'alta' ? 'border-orange-500 bg-orange-50' : ''}`}>
                <RadioGroupItem value="alta" id="edit_prioridade_alta" className="sr-only" />
                <Label htmlFor="edit_prioridade_alta" className="cursor-pointer text-center">
                  <span className="w-3 h-3 rounded-full bg-orange-500 inline-block mb-1"></span>
                  <span className="block font-medium">Alta</span>
                </Label>
              </div>
              <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${prioridade === 'estrategica' ? 'border-red-500 bg-red-50' : ''}`}>
                <RadioGroupItem value="estrategica" id="edit_prioridade_estrategica" className="sr-only" />
                <Label htmlFor="edit_prioridade_estrategica" className="cursor-pointer text-center">
                  <span className="w-3 h-3 rounded-full bg-red-500 inline-block mb-1"></span>
                  <span className="block font-medium">Estrategica</span>
                </Label>
              </div>
            </RadioGroup>
          ) : (
            <div>
              <Label>Justificativa / Detalhamento</Label>
              <Textarea
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={12}
                className="mt-2 font-mono text-sm"
                placeholder="Descreva a justificativa..."
              />
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
